/**
 * Manual checkout / checkin API client. Talks to MTTR-native
 * /api/manual/* routes (not the legacy rebound-smart endpoints).
 *
 *   POST /api/manual/lookup      validate scanned EPCs
 *   GET  /api/manual/profile     per-smartbox field profile (migration 107)
 *   POST /api/manual/checkout    perform MC; capability: inventory.manual_checkout
 *   POST /api/manual/checkin     perform MI; capability: inventory.manual_checkin
 *   GET  /api/manual/history     paginated MC/MI history
 *   GET  /api/manual/ticket/{id} single ticket + lines
 *
 * The field set captured on each ticket is customer-driven. The
 * client fetches the profile after resolving which smartbox all the
 * scanned tags belong to, then renders the fields it returns.
 */
import { apiFetch } from "./apiClient";

export type ManualKind = "MC" | "MI";

/** Field-def type vocabulary — mirrors mttr.adapters.manual_db._FIELD_TYPES. */
export type ManualFieldType =
  | "text"
  | "textarea"
  | "barcode"
  | "signature"
  | "select";

export interface ManualFieldDef {
  key: string;
  label: string;
  type: ManualFieldType;
  required?: boolean;
  placeholder?: string;
  max_length?: number;
  select_options?: string[];
  /** Layout hint 1-6 (out of a 6-col grid). Default 6 = full row.
   *  Purely cosmetic — server ignores it. */
  column_span?: number;
}

export interface ManualProfile {
  profile_key: string;
  display_name: string;
  mc_enabled: boolean;
  mi_enabled: boolean;
  mc_fields: ManualFieldDef[];
  mi_fields: ManualFieldDef[];
  /** Platform callers only: true when mc_enabled/mi_enabled above are open
   *  because the caller is platform, not because the customer switched
   *  manual on for this box (migration 127's per-smartbox flags). Lets the
   *  page say so instead of implying the customer opted in. */
  enablement_bypassed?: boolean;
}

export interface ManualLookupRow {
  epc: string;
  found: boolean;
  smartbox_id: number | null;
  smartbox_code: string | null;
  workcenter_id: number | null;
  workcenter_name: string | null;
  customer_company_id: number | null;
  item_id: number | null;
  sku: string | null;
  item_name: string | null;
  vendor_company_id: number | null;
  vendor_name: string | null;
  lifecycle_status: string | null;
  md_status: number | null;
  can_checkout: boolean;
  can_checkin: boolean;
}

export interface ManualLookupResponse {
  rfids: ManualLookupRow[];
}

/** Payload sent to /checkout and /checkin. `custom_fields` is keyed by
 *  profile-field `key`; signature-type fields carry a base64 data URL. */
export interface ManualSubmitBody {
  smartbox_id: number;
  rfids: string[];
  custom_fields: Record<string, string>;
}

export interface ManualSubmitResult {
  ticket_id: number;
  doc_num: string;
  kind: ManualKind;
  profile_key: string;
  processed: string[];
  errors: { epc: string; error: string }[];
  /** Final custom_fields as stored (signature dataURLs replaced with
   *  Azure URLs on successful upload). */
  custom_fields: Record<string, string>;
  /** Per-signature-key blob URLs (empty when none uploaded). */
  signature_urls: Record<string, string>;
  /** Set on partial failure — no eligible tags, signature-upload failure, etc. */
  warning?: string;
}

export interface ManualHistoryRow {
  ticket_id: number;
  doc_num: string;
  kind: ManualKind;
  smartbox_id: number;
  smartbox_code: string | null;
  workcenter_name: string | null;
  customer_company_id: number | null;
  customer_name: string | null;
  profile_key: string | null;
  custom_fields: Record<string, string>;
  line_count: number;
  line_total: number | null;
  created_by_user_id: number;
  created_by_name: string | null;
  created_utc: string;
}

export interface ManualHistoryResponse {
  data: ManualHistoryRow[];
  pagination: { total: number; limit: number; offset: number };
}

export interface ManualTicketLine {
  line_id: number;
  epc: string;
  item_id: number | null;
  sku: string | null;
  item_name: string | null;
  vendor_company_id: number | null;
  unit_price: number | null;
  currency: string | null;
  customer_part_no: string | null;
  line_note: string | null;
  ledger_entry_id: number | null;
}

export interface ManualTicketDetail extends ManualHistoryRow {
  lines: ManualTicketLine[];
}

async function req<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  return apiFetch<T>(path, token, init);
}

export function lookupManualTags(
  token: string, rfids: string[],
): Promise<ManualLookupResponse> {
  return req<ManualLookupResponse>(token, "/api/manual/lookup", {
    method: "POST",
    body: JSON.stringify({ rfids }),
  });
}

export function fetchManualProfile(
  token: string, smartboxId: number,
): Promise<ManualProfile> {
  return req<ManualProfile>(
    token, `/api/manual/profile?smartbox_id=${encodeURIComponent(smartboxId)}`,
  );
}

/** One entry in the manual check-in destination picker (migration 127):
 *  a smartbox the caller can see that has manual check-in enabled — or,
 *  for a platform caller, any smartbox they can see (2026-09-18). */
export interface ManualCheckinBox {
  smartbox_id: number;
  smartbox_code: string | null;
  scu_name: string | null;
}

export function fetchManualCheckinBoxes(
  token: string,
): Promise<{ boxes: ManualCheckinBox[] }> {
  return req<{ boxes: ManualCheckinBox[] }>(token, "/api/manual/checkin-boxes");
}

export function submitManualCheckout(
  token: string, body: ManualSubmitBody,
): Promise<ManualSubmitResult> {
  return req<ManualSubmitResult>(token, "/api/manual/checkout", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function submitManualCheckin(
  token: string, body: ManualSubmitBody,
): Promise<ManualSubmitResult> {
  return req<ManualSubmitResult>(token, "/api/manual/checkin", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function fetchManualHistory(
  token: string,
  q: {
    kind?: ManualKind; since?: string; until?: string;
    smartbox?: number; limit?: number; offset?: number;
  } = {},
): Promise<ManualHistoryResponse> {
  const p = new URLSearchParams();
  if (q.kind) p.set("kind", q.kind);
  if (q.since) p.set("since", q.since);
  if (q.until) p.set("until", q.until);
  if (q.smartbox != null) p.set("smartbox", String(q.smartbox));
  if (q.limit != null) p.set("limit", String(q.limit));
  if (q.offset != null) p.set("offset", String(q.offset));
  const qs = p.toString();
  return req<ManualHistoryResponse>(token, `/api/manual/history${qs ? `?${qs}` : ""}`);
}

export function fetchManualTicket(
  token: string, ticketId: number,
): Promise<ManualTicketDetail> {
  return req<ManualTicketDetail>(token, `/api/manual/ticket/${ticketId}`);
}
